// createCategoryLoader.js
import { createInitialStoreWithRequest } from '../createInitialStore.js';
import { legacySSR as legacySSRDebug } from '../resource/debug.js';
import getHttpProxyHeaders from '../resource/getHttpProxyHeaders.js';
import generateCanonicalAndAlternates from '../resource/generateCanonicalAndAlternates.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import { SUPPORTED_LANGUAGES } from '../RemoteConfigKeys.js';
import { prepareCollectionPageMeta } from '../resource/prepareCollectionPageMeta.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import { getFeedCategory } from '../resource/feedUtils.js';
import getTranslationKey from '../resource/getTranslationKey.js';

/**
 * Create a loader for category pages
 * @param {Object} config
 * @param {string} config.pathPrefix - Path prefix for canonical URL (e.g., 'user-categories', 'story-categories')
 * @param {string} config.categoryType - Category type for keywords (e.g., 'creators', 'story', 'shorts', 'video')
 * @param {string} config.loaderName - Name for logging (e.g., 'UserCategoriesCategory')
 * @returns {Function} Loader function
 */
export const createCategoryLoader = ({
  pathPrefix,
  categoryType,
  loaderName,
}) => {
  const legacySSRLog = legacySSRDebug.extend('log');
  const legacySSRError = legacySSRDebug.extend('error');

  return async ({ context, params, request, i18nInstance, language }) => {
    const log = legacySSRLog.extend(`${loaderName} loader()`);
    const errorLog = legacySSRError.extend(`${loaderName} loader()`);
    log('start', { context, params, request });

    try {
      const httpProxyHeaders = getHttpProxyHeaders({
        request,
      });
      log('getHttpProxyHeaders', { httpProxyHeaders });

      const { store, initI18nPromise } = await createInitialStoreWithRequest({
        params,
        request,
      });

      // Wait for i18n to initialize
      await initI18nPromise;

      log('createInitialStoreWithRequest', { store: store.getState().toJS() });

      const state = store.getState();
      const { category } = params;

      // Use i18n instance from loader
      const i18n = i18nInstance;

      // Get category name from translation.
      // Fold in the feed query (e.g. `?ui=flix`) and normalize the key, so it
      // matches the translation entry which is keyed by the full alias-with-query
      // (e.g. `pro_produce_a_ui_flix`). Without this the title falls back to the
      // untranslated bare alias.
      const { search } = new URL(request.url);
      const feedCategory = getFeedCategory({ category, search });
      const titleI18nId = getTranslationKey({ key: feedCategory });
      const title =
        i18n?.t(titleI18nId, {
          ns: TranslationNamespace.FEED,
        }) || category;

      const availableLanguages = getRemoteConfigData(
        state,
        SUPPORTED_LANGUAGES
      );
      const canonicalPath = `/${pathPrefix}/${category}`;
      const { canonical, alternates } = generateCanonicalAndAlternates({
        canonicalPath,
        availableLanguages,
        language,
      });

      // Prepare keywords based on category type
      const keywords = [title, categoryType, 'SWAG'];
      // Add extra keywords for user categories
      if (categoryType === 'creators') {
        keywords.push('users');
      }

      // Prepare meta data for SEO in loader
      const meta = await prepareCollectionPageMeta({
        state,
        title,
        keywords,
        hostname: request?.headers?.get('host') || 'swag.live',
        canonical,
        alternates,
      });

      log('meta data prepared', { meta });

      return {
        data: { store: {} },
        params,
        meta,
        staticContext: {
          requestHeaders: {
            hostname: request?.headers?.get('host') || 'swag.live',
          },
        },
      };
    } catch (error) {
      errorLog('error', { error, params });
      return { data: null, error, params };
    }
  };
};

export default createCategoryLoader;
